import { useMemo, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { selectUser } from '../store/slices/authSlice';
import { selectCombinedPermissions } from '../store/slices/permissionsSlice';
import { selectSubscription } from '../store/slices/subscriptionSlice';
import { fetchPlanFeatureToggles, selectGlobalToggles } from '../store/slices/featureTogglesSlice';
import { resolveFeatureToggleAccess } from '../utils/featureToggleAccess';

/**
 * Feature Toggle Hook
 * Checks feature visibility based on:
 * 1. Plan-based toggles (from permissions)
 * 2. User-specific toggles (from user.featureToggles)
 * 3. Page-level toggles
 * 4. Component-level toggles
 *
 * Priority: User-specific > Plan-based > Default
 */
export function useFeatureToggle(featureKey, options = {}) {
  const user = useAppSelector(selectUser);
  const subscription = useAppSelector(selectSubscription);
  const dispatch = useAppDispatch();
  const globalToggles = useAppSelector(selectGlobalToggles);

  // Get plan ID from subscription
  const planId = subscription?.planId || subscription?.plan?.id;

  // Fetch plan toggles if we have a plan ID
  useEffect(() => {
    if (planId && !user?.role) {
      // Only fetch for non-admin users
      dispatch(fetchPlanFeatureToggles(planId)).catch(err => {
        console.warn('Failed to fetch plan feature toggles:', err);
      });
    }
  }, [planId, dispatch, user?.role]);

  const { defaultValue = true } = options;

  return useMemo(
    () => resolveFeatureToggleAccess(user, globalToggles, featureKey, defaultValue),
    [user, featureKey, defaultValue, globalToggles]
  );
}

/**
 * Hook to check if a page is accessible
 */
export function usePageAccess(pagePath) {
  const user = useAppSelector(selectUser);
  const permissions = useAppSelector(selectCombinedPermissions);

  return useMemo(() => {
    // Admins always have access
    if (user?.role === 'admin' || user?.role === 'super-admin') {
      return true;
    }

    if (!permissions) return false;

    // Check user-specific page toggles
    if (user?.pageToggles && typeof user.pageToggles === 'object') {
      if (Object.prototype.hasOwnProperty.call(user.pageToggles, pagePath)) {
        return user.pageToggles[pagePath] === true;
      }
    }

    // Check plan-based page access
    if (permissions.restrictedPages?.includes(pagePath)) {
      return false;
    }
    if (permissions.allowedPages?.includes(pagePath)) {
      return true;
    }

    return false;
  }, [user, permissions, pagePath]);
}

/**
 * Hook to get all feature toggles for current user
 */
export function useAllFeatureToggles() {
  const user = useAppSelector(selectUser);
  const permissions = useAppSelector(selectCombinedPermissions);

  return useMemo(() => {
    const toggles = {
      // Plan-based features
      planFeatures: permissions?.allowedFeatures || [],
      restrictedFeatures: permissions?.restrictedFeatures || [],
      // User-specific features
      userFeatures: user?.featureToggles || {},
      // Page access
      allowedPages: permissions?.allowedPages || [],
      restrictedPages: permissions?.restrictedPages || [],
      userPageToggles: user?.pageToggles || {},
    };

    return toggles;
  }, [user, permissions]);
}

export default useFeatureToggle;
